import { useState } from "react";
import { Icon } from "../../components/Icon";
import { Badge, Card, Empty, PageHead, Skeleton } from "../../components/kit";
import { useHistory, useSaved, useThreads } from "../../lib/hooks";

function ConvSkeleton() {
  return (
    <div className="conv-list">
      {Array.from({ length: 5 }).map((_, i) => (
        <div className="conv" key={i} style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Skeleton w={32} h={32} r={9} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
            <Skeleton w="60%" h={13} /><Skeleton w={90} h={11} />
          </div>
        </div>
      ))}
    </div>
  );
}

const modelLabel = (model: string) =>
  /opus/i.test(model) ? "Opus" : /sonnet/i.test(model) ? "Sonnet" : /haiku/i.test(model) ? "Haiku" : model || "—";

export function HistoryPage() {
  const live = useHistory();
  const { items: saved, save, remove } = useSaved();
  const { threads, retain } = useThreads();
  const [tab, setTab] = useState("Conversations");
  const [justSaved, setJustSaved] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const onSave = (id: string, title: string) => {
    setJustSaved((s) => new Set(s).add(id)); // optimistic check on the row
    save(id, title).catch(() => {});
  };
  const toggle = (id: string) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const copy = (text: string) => navigator.clipboard?.writeText(text).catch(() => {});

  return (
    <div className="page">
      <PageHead
        title="History"
        sub="Everything you’ve asked Orbit and the answers you saved."
        actions={<div className="search-inline"><Icon name="search" size={15} /><input placeholder="Search history…" /></div>}
      />

      <div className="lessons-bar">
        <div className="seg">
          {["Conversations", "Threads", "Saved"].map((t) => (
            <button key={t} aria-pressed={tab === t} onClick={() => setTab(t)}>
              {t}
              {t === "Threads" && threads && threads.length > 0 ? ` (${threads.length})` : ""}
              {t === "Saved" && saved && saved.length > 0 ? ` (${saved.length})` : ""}
            </button>
          ))}
        </div>
      </div>

      {tab === "Conversations" && (
        <Card pad={false}>
          {live === null ? (
            <ConvSkeleton />
          ) : live.length === 0 ? (
            <Empty icon="message-square">No conversations yet — ask Orbit something to get started.</Empty>
          ) : (
            <div className="conv-list">
              {live.map((h, i) => {
                const isSaved = justSaved.has(h.id);
                const isOpen = expanded.has(h.id);
                return (
                  <div className="conv-item" key={h.id ?? i}>
                    <div className="conv conv-clickable" onClick={() => toggle(h.id)}>
                      <span className="conv-ic"><Icon name={h.ic} size={16} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="conv-t">{h.t}</div>
                        <div className="conv-meta">
                          <Badge tone={h.mode === "Coach" ? "live" : "neutral"}>{h.mode}</Badge>
                          {h.hadScreenshot && <span className="conv-tag"><Icon name="image" size={11} />Screen</span>}
                          <span className="conv-ago">{h.ago}</span>
                        </div>
                      </div>
                      <button
                        className="icon-mini"
                        title={isSaved ? "Saved" : "Save to your snippets"}
                        disabled={isSaved || !h.id}
                        onClick={(e) => { e.stopPropagation(); h.id && onSave(h.id, h.t); }}
                      >
                        <Icon name={isSaved ? "bookmark-check" : "bookmark"} size={15} color={isSaved ? "var(--live-dim)" : undefined} />
                      </button>
                      <Icon name="chevron-down" size={16} className={`conv-chev ${isOpen ? "open" : ""}`} />
                    </div>
                    {isOpen && (
                      <div className="conv-detail">
                        <div className="cd-block">
                          <div className="cd-label">You asked</div>
                          <div className="cd-text">{h.userInput || "(no transcript stored)"}</div>
                        </div>
                        <div className="cd-block">
                          <div className="cd-label">Orbit replied</div>
                          <div className="cd-text">{h.assistantOutput || "(no transcript stored)"}</div>
                        </div>
                        <div className="cd-foot">
                          <span className="cd-chip"><Icon name="cpu" size={12} />{modelLabel(h.model)}</span>
                          <span className="cd-chip"><Icon name="zap" size={12} />{h.credits} credit{h.credits === 1 ? "" : "s"}</span>
                          <span className="cd-chip"><Icon name="hash" size={12} />{(h.inputTokens + h.outputTokens).toLocaleString()} tokens</span>
                          {h.hadScreenshot && <span className="cd-chip"><Icon name="image" size={12} />screenshot attached</span>}
                          {h.assistantOutput && <button className="cd-copy" onClick={() => copy(h.assistantOutput)}><Icon name="copy" size={12} />Copy reply</button>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {tab === "Threads" && (
        <Card pad={false}>
          {!retain ? (
            <Empty icon="history">Resume is off. Turn on <b>Store conversation history</b> in Settings to keep threads you can pick back up.</Empty>
          ) : threads === null ? (
            <ConvSkeleton />
          ) : threads.length === 0 ? (
            <Empty icon="message-square">No threads yet. Start a lesson or ask Orbit something — you can stop and continue right here.</Empty>
          ) : (
            <div className="conv-list">
              {threads.map((t) => (
                <div className="conv" key={t.id}>
                  <span className="conv-ic"><Icon name={t.mode === "coach" ? "graduation-cap" : "message-square"} size={16} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="conv-t">{t.title}</div>
                    <div className="conv-meta">
                      <Badge tone={t.mode === "coach" ? "live" : "neutral"}>{t.mode === "coach" ? "Lesson" : "Helper"}</Badge>
                      <span className="conv-ago">{t.turns} turn{t.turns === 1 ? "" : "s"} · {t.ago}</span>
                    </div>
                  </div>
                  <a className="btn ghost sm" href={`adaptr://lesson?thread=${encodeURIComponent(t.id)}`} title="Open Orbit and continue this thread">
                    <Icon name="play" size={14} />Continue
                  </a>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "Threads" && threads && threads.length > 0 && (
        <p style={{ fontSize: 12, color: "var(--fg-3)", margin: "0 2px", lineHeight: 1.5 }}>
          “Continue” opens the Orbit desktop app and picks up right where you left off — even across Helper and lessons.
        </p>
      )}

      {tab === "Saved" && (
        <Card title="Saved answers & snippets" icon="bookmark" count={saved?.length} pad={false}>
          {saved === null ? (
            <ConvSkeleton />
          ) : saved.length === 0 ? (
            <Empty icon="bookmark">Nothing saved yet — tap the bookmark on any conversation to keep it here.</Empty>
          ) : (
            <div className="conv-list">
              {saved.map((s) => (
                <div className="conv" key={s.id}>
                  <span className="conv-ic"><Icon name="bookmark" size={16} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="conv-t">{s.title}</div>
                    {s.body && <div className="conv-snippet">{s.body}</div>}
                  </div>
                  <button className="icon-mini" title="Copy answer" disabled={!s.body} onClick={() => copy(s.body || s.title)}><Icon name="copy" size={15} /></button>
                  <button className="icon-mini" title="Remove" onClick={() => remove(s.id)}><Icon name="x" size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
